import {Component, OnInit, ViewChild} from '@angular/core';
import {DatePipe, NgTemplateOutlet} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {forkJoin} from 'rxjs';
import {FormHttpService} from '../data-access/form-http.service';
import {FormRequirementHttpService} from '../data-access/form-requirement-http.service';
import {EmployeeHttpService} from '../../company/x-http-requests/employee-http.service';
import {EmploymentRelationshipHttpService} from '../../company/x-http-requests/employment-relationship-http.service';
import {
  BackfillPolicy,
  backfillPolicyLabel,
  defaultGatingDraft,
  existingStaffPolicyHint,
  FormRequirementResource,
  gatingDraftOf,
  GRACE_DAYS_DEFAULT,
  GRACE_DAYS_MAX,
  GRACE_DAYS_MIN,
  isValidGraceDays,
  newStaffPolicyHint,
  RequirementGatingDraft,
  RequirementTargetType
} from '../shared/models/form-requirement.model';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlertLevel} from '../../../enums/alert-level';
import {SelectComponent} from '../../../_ui/components/controls/select/select.component';

/**
 * One entry of the target picker.
 *
 * `name` is the whole option text — the employee's name, or the relationship's — and nothing else. That is
 * deliberate: it is what `<cqme-select>` searches on, so a manager types "Lovelace" and lands on her. The
 * internal id travels as the *value*, never as text, so it cannot be fished for.
 */
interface Target {
  id: string;
  name: string;
}

/** One entry of a cohort policy dropdown. Held as a frozen constant — see {@link FormRequirementsComponent.policyOptions}. */
interface PolicyOption {
  label: string;
  value: BackfillPolicy;
}

/**
 * Manager UI to make a form **required** for employees / employment-relationships ("Pflicht-Zuweisung"). Reached
 * from the form manager (`/company/forms/requirements?id={formKey}`). Lists the form's current requirements with
 * resolved target names and lets a manager add, **edit** or remove one. Backed by the manager-gated
 * `/api/forms/v2025.1/requirement` API; see docs/articles/forms/required-forms-gating.md.
 *
 * <h3>Two cohorts, never one policy (#504)</h3>
 * A grace window belongs to a *person*, not to a requirement. An assignment therefore carries two independent
 * policies — one for staff employed *without interruption* since before it took effect, one for everyone whose
 * current stint began at or after that point — plus the window length they share. The headline configuration is
 * the asymmetric one ("the existing workforce gets a fortnight, new hires fill it in before they start"), which
 * a single policy could not express at all.
 *
 * The second cohort is **not** only new hires: the anchor is the start of the *current* employment, so someone
 * who left and came back lands here too. The label says so ("Neu eingetretene und zurückkehrende Mitarbeiter"),
 * because an author who read it as "Neueinstellungen" would configure the wrong half for a rehire.
 *
 * The two are worded differently on screen **on purpose**: the existing-staff window ends on one shared date, so
 * it is shown as a date; the other window starts when that person starts, so it is only ever shown as a
 * duration. Showing a fixed date for that cohort would be false for every employee it was shown to.
 *
 * <h3>The list is a table of settings, not a strip of tags (#501)</h3>
 * "Aktuelle Zuweisungen" is the repo's standard admin list — an **aligned table at `md`+, stacked cards below
 * it** (style guide, "Data table + card fallback"). It got there by way of two rejected attempts at a wrapping
 * chip strip, and the reasons it is a table now are worth keeping written down:
 * <ul>
 *   <li><b>The cohort names belong in the column headings, not on every row.</b> Each assignment states two
 *       *values*; the two labels are constant down the whole list. Repeating "Bestehende:" and
 *       "Neu &amp; Wiedereintritt:" on every row spent the row's whole width on words the reader already knew,
 *       leaving the actual answer ("Frist bis 12.09.2026") as the quietest thing on the line.</li>
 *   <li><b>One badge per cell, carrying the whole value — and exactly two colours.</b> What made the original
 *       row unreadable was not the badge: it was a badge <i>and</i> a separate grey qualifier beside it
 *       <i>and</i> no columns, five elements of near-equal weight per row with the real answer the quietest of
 *       them. With the labels lifted into the column headings there is one thing left to say per cell, so it is
 *       said once, in a badge — and the colour is then worth having, because it makes the state readable
 *       without reading: <b>red = blocked right now</b> („Sofort gesperrt"), <b>amber = a window is running</b>
 *       („Frist bis 12.09.2026", „30 Tage ab Eintritt", and the undated „Frist von 21 Tagen", which is a window
 *       too). Two colours, two meanings, nothing else on the list coloured at all. The pairs are measured, not
 *       assumed — `amber-700` on `amber-50` is 4.84:1 and `red-700` on `red-50` is 5.91:1 at the badge's
 *       `text-xs`, where `amber-600` (3.07:1) and `red-600` (4.41:1) both fail AA; the spec pins that with
 *       axe's `color-contrast` rule rather than trusting the note.</li>
 *   <li><b>Alignment has to be structural.</b> The previous rows each wrapped independently, so three
 *       assignments produced three differently-shaped blocks and could not be compared at a glance. A
 *       `table-fixed` table makes "every value in a column shares one left edge, and the action column one
 *       right edge" a property of the markup rather than something that happens to hold for the current data.
 *       That is the whole reason the question "which of these locks people out immediately?" is now answerable
 *       by reading straight down.</li>
 * </ul>
 *
 * <h3>Below `md`, the labels come back</h3>
 * The card fallback has no column headings to inherit from, so each card restates them as a `<dl>`: the cohort
 * name is the `<dt>`, its value the `<dd>`. That is also what keeps the fallback *announced* correctly — the
 * table's `<th scope="col">` associations have no equivalent in a stack of cards, and an unlabelled
 * "Sofort gesperrt" in a card is not a fact anyone can use.
 *
 * Both layouts are in the DOM at once (Tailwind's `hidden md:block` / `md:hidden`), so the row actions, the two
 * cohort values and the in-place editor are each defined **once** as an `ng-template` and rendered twice — a
 * copy-paste pair would be two places for the next change to miss. The editor's field ids are keyed on a
 * `prefix` for the same reason they always were: `edit` in the table, `edit-card` in the cards, so the two
 * copies cannot collide on an id.
 *
 * <h3>Editing is in place, not in a dialog</h3>
 * The row expands into an editor that renders the very same fields as the "new assignment" card (one
 * `ng-template`, used twice). Three reasons that beat a modal here: the fields are already on the page a few
 * centimetres above, so there is nothing to re-learn; no overlay means no focus trap, no scroll lock and no
 * z-index question to get wrong; and `<cqme-select>` inside an overlay drags in the whole `[appendTo]` problem
 * (a panel that must be portaled out of the dialog's clip, then kept positioned on scroll — see the style
 * guide's ng-select gotcha), which an inline editor simply does not have.
 */
@Component({
  selector: 'app-form-requirements',
  standalone: true,
  imports: [DatePipe, FormsModule, NgTemplateOutlet, RouterLink, SelectComponent],
  template: `
    <div class="px-4 sm:px-6 lg:px-8 py-4 absolute inset-0 overflow-y-auto">
      <div class="mx-auto w-full max-w-3xl flex flex-col gap-4">

        <a routerLink="/company/forms"
           class="inline-flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
          </svg>
          Formulare
        </a>

        <div>
          <h1 class="text-xl font-bold tracking-tight text-gray-900">Pflicht-Zuweisung</h1>
          <p class="mt-1 text-sm text-gray-500">Legen Sie fest, für wen <span class="font-semibold text-gray-700">{{ formTitle }}</span> ein Pflichtformular ist.</p>
        </div>

        @if (loading) {
          <div class="flex items-center justify-center py-20">
            <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
            </svg>
          </div>
        } @else if (error) {
          <!-- red-700, not red-600: red-600 on red-50 measures 4.41:1 and on the button's red-100 only 3.95:1,
               both under the 4.5:1 floor. Caught by the axe check in this component's spec. -->
          <div class="p-4 bg-red-50 border-[1px] border-red-100 rounded-2xl shadow-sm">
            <p class="text-sm font-semibold text-red-700">Die Zuweisungen konnten nicht geladen werden.</p>
            <button (click)="load()" class="mt-2 inline-flex items-center text-red-700 bg-red-100 hover:bg-red-200 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200">Erneut versuchen</button>
          </div>
        } @else {

          <!-- Add -->
          <div class="p-4 bg-white rounded-2xl border-[1px] border-gray-100 shadow-sm space-y-3">
            <p class="text-base font-bold text-gray-800 tracking-tight">Neue Zuweisung</p>

            <!-- target type. The SELECTED tab is theme-primary-700, not -600: the theme is Tailwind's pink, and
                 pink-600 on pink-50 measures 4.2:1 — under the floor for 14px text. Caught by the axe check. -->
            <div class="inline-flex rounded-xl border-[1px] border-gray-200 bg-white p-1 shadow-sm w-full sm:w-auto">
              <button type="button" (click)="setTargetType(TargetType.Employee)"
                      class="flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap"
                      [class.bg-theme-primary-50]="targetType === TargetType.Employee"
                      [class.text-theme-primary-700]="targetType === TargetType.Employee"
                      [class.shadow-sm]="targetType === TargetType.Employee"
                      [class.text-gray-500]="targetType !== TargetType.Employee">Mitarbeiter</button>
              <button type="button" (click)="setTargetType(TargetType.EmploymentRelationship)"
                      class="flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap"
                      [class.bg-theme-primary-50]="targetType === TargetType.EmploymentRelationship"
                      [class.text-theme-primary-700]="targetType === TargetType.EmploymentRelationship"
                      [class.shadow-sm]="targetType === TargetType.EmploymentRelationship"
                      [class.text-gray-500]="targetType !== TargetType.EmploymentRelationship">Arbeitsverhältnis</button>
            </div>

            <!-- target -->
            <div>
              <div class="grow">
                <!-- No for= attribute: the control is the shared cqme-select, whose focusable element is
                     ng-select's own search input. It carries the same wording through [ariaLabel], so the
                     accessible name is identical — pinned by this component's axe spec.
                     (No backticks in this comment: the whole template is a TS template literal.) -->
                <label class="block text-sm font-bold text-gray-700 mb-2 tracking-tight">
                  {{ targetType === TargetType.Employee ? 'Mitarbeiter' : 'Arbeitsverhältnis' }}
                </label>
                <!-- The shared <cqme-select>, never a native <select>. A native dropdown's browser type-ahead
                     matches an option from the first characters of its text and its list ignores the theme
                     tokens; here the option text is the person's (or the relationship's) name, which is exactly
                     what ng-select searches — so a manager finds "Lovelace" by typing it, and no internal id is
                     reachable through the picker at all. -->
                <cqme-select #targetPicker data-testid="target-select" class="block w-full"
                             [options]="availableTargets" [labelKey]="'name'" [valueKey]="'id'"
                             size="md" [clearable]="true" placeholder="Bitte wählen…"
                             [ariaLabel]="targetType === TargetType.Employee ? 'Mitarbeiter' : 'Arbeitsverhältnis'"
                             [current]="selectedTargetId"
                             (currentChange)="setSelectedTarget($event)"></cqme-select>
                @if (availableTargets.length === 0) {
                  <!-- gray-600, not gray-400: gray-400 measures 2.4:1 here, nowhere near readable. -->
                  <p class="mt-2 text-xs text-gray-600">Alle {{ targetType === TargetType.Employee ? 'Mitarbeiter' : 'Arbeitsverhältnisse' }} sind bereits zugewiesen.</p>
                }
              </div>
            </div>

            <ng-container [ngTemplateOutlet]="gatingFields"
                          [ngTemplateOutletContext]="{$implicit: newAssignment, prefix: 'create'}"></ng-container>

            <div class="flex justify-end">
              <button type="button" (click)="add()" [disabled]="!canSubmitNew"
                      class="inline-flex items-center justify-center px-5 py-2.5 bg-theme-primary-600 text-white rounded-2xl hover:bg-theme-primary-700 font-semibold shadow-lg shadow-theme-primary-100 hover:shadow-xl transition-all duration-200 active:scale-95 disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed">
                Zuweisen
              </button>
            </div>
          </div>

          <!-- Current requirements -->
          <div>
            <p class="text-base font-bold text-gray-800 tracking-tight mb-2" id="assignments-heading">Aktuelle Zuweisungen</p>
            @if (requirements.length === 0) {
              <!-- gray-500, not gray-400: an empty state is still text, and gray-400 measures 2.4:1 on the page. -->
              <p class="text-sm text-gray-500 italic py-8 border-2 border-dashed border-gray-100 rounded-2xl text-center">
                Dieses Formular ist für niemanden verpflichtend.
              </p>
            } @else {

              <!--
                md and up: aligned columns. The cohort names are the column HEADINGS, so a row carries only its
                two values — and because the table is table-fixed, every value in a column shares one left edge
                and the action column one right edge no matter what the data is.
                (No backticks anywhere in these comments: the whole template is a TS template literal.)

                border-[1px], NOT the bare "border" — Bootstrap is loaded globally (angular.json -> build
                styles) and ships its own .border utility with !important, which beats border-gray-100 and
                paints the card outline in Bootstrap's #dee2e6 instead of gray-100's #f3f4f6. On a short list
                that lands as two dark rules bracketing the row while the real row divider stays invisible: the
                card stops reading as a card. The arbitrary-value form has no Bootstrap counterpart, so the
                colour applies. Same reason everywhere else on this page. See the style guide's Bootstrap
                gotcha (#231/#234) — the class linter does not catch this one, "border" is not in its token
                list, so the spec asserts it instead.
              -->
              <div class="hidden md:block overflow-x-auto rounded-2xl border-[1px] border-gray-100 bg-white shadow-sm"
                   data-test="assignments-table">
                <table class="min-w-full table-fixed divide-y divide-gray-100 text-sm"
                       aria-labelledby="assignments-heading">
                  <thead class="bg-gray-50">
                    <tr class="text-left text-xs uppercase tracking-wide text-gray-500">
                      <!-- The widths are pinned rather than left to the browser, and they are what makes the
                           alignment hold across DATA as well as across rows: an auto-layout table re-measures
                           its columns from the longest cell, so one long name would quietly narrow the value
                           columns until "Frist bis 12.09.2026" broke over two lines. w-1/3 + w-28 leaves each
                           value column ~180px of content — one line for every value this screen can produce,
                           including the 3650-day ceiling. -->
                      <th scope="col" class="w-1/3 px-4 py-3 font-semibold">Ziel</th>
                      <th scope="col" class="px-4 py-3 font-semibold">Bestehende Belegschaft</th>
                      <th scope="col" class="px-4 py-3 font-semibold">Neu &amp; Wiedereintritt</th>
                      <th scope="col" class="w-28 px-4 py-3 text-right font-semibold">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    @for (r of requirements; track r.id) {
                      <!-- align-top, not align-middle: a wrapped long name must not push its row's values off
                           the line the other rows' values sit on. -->
                      <tr class="transition-colors hover:bg-gray-50">
                        <!--
                          EVERY cell's content sits in the same min-h-8 box, centred. The action buttons are
                          h-8 (32px) while a badge is ~20px and a name's line ~20px, so with align-top alone
                          three different box heights start at one top edge and their CENTRES land 6px apart —
                          the badges float above the pencil/trash. That is what "the badges are not aligned
                          with the icons" means, and why fixing it at the cell level (padding, vertical-align,
                          the badge's own baseline) never worked: none of those close a gap created by
                          differing box heights under top alignment. A shared minimum line box makes the
                          centres identical by construction; content taller than 32px (a wrapped name) simply
                          grows downward.

                          No backticks in this comment, deliberately: the template is a template literal, so
                          one would end it here and the rest of the component would be parsed as code.
                        -->
                        <th scope="row" class="px-4 py-3 align-top text-left font-normal">
                          <div class="flex min-h-8 flex-col justify-center">
                            <span class="block font-semibold text-gray-900 break-words"
                                  data-test="target-name">{{ targetName(r) }}</span>
                            <span class="mt-0.5 block text-xs text-gray-500"
                                  data-test="target-type">{{ targetTypeLabel(r) }}</span>
                          </div>
                        </th>
                        <td class="px-4 py-3 align-top" data-test="existing-staff-value">
                          <div class="flex min-h-8 items-center">
                            <ng-container [ngTemplateOutlet]="existingStaffValue"
                                          [ngTemplateOutletContext]="{$implicit: r}"></ng-container>
                          </div>
                        </td>
                        <td class="px-4 py-3 align-top" data-test="new-staff-value">
                          <div class="flex min-h-8 items-center">
                            <ng-container [ngTemplateOutlet]="newStaffValue"
                                          [ngTemplateOutletContext]="{$implicit: r}"></ng-container>
                          </div>
                        </td>
                        <td class="px-4 py-3 align-top">
                          <div class="flex min-h-8 items-center justify-end gap-1" data-test="row-actions">
                            <ng-container [ngTemplateOutlet]="rowActions"
                                          [ngTemplateOutletContext]="{$implicit: r}"></ng-container>
                          </div>
                        </td>
                      </tr>
                      @if (editingId === r.id && editDraft) {
                        <tr>
                          <td colspan="4" class="px-4 pb-4">
                            <ng-container [ngTemplateOutlet]="rowEditor"
                                          [ngTemplateOutletContext]="{$implicit: r, prefix: 'edit'}"></ng-container>
                          </td>
                        </tr>
                      }
                    }
                  </tbody>
                </table>
              </div>

              <!--
                Below md: one card per assignment. There are no column headings to inherit from here, so each
                card restates the two cohort names as a description list — the label is the dt, the value the
                dd. That is what keeps an isolated "Sofort gesperrt" meaningful, on screen and to a reader.
              -->
              <ul class="grid grid-cols-1 gap-3 md:hidden" data-test="assignments-cards">
                @for (r of requirements; track r.id) {
                  <li class="rounded-2xl border-[1px] border-gray-100 bg-white p-3 shadow-sm">
                    <div class="flex items-start gap-3">
                      <div class="min-w-0 flex-1">
                        <p class="text-sm font-semibold text-gray-900 break-words"
                           data-test="target-name">{{ targetName(r) }}</p>
                        <p class="mt-0.5 text-xs text-gray-500"
                           data-test="target-type">{{ targetTypeLabel(r) }}</p>
                      </div>
                      <div class="shrink-0 flex items-center gap-1" data-test="row-actions">
                        <ng-container [ngTemplateOutlet]="rowActions"
                                      [ngTemplateOutletContext]="{$implicit: r}"></ng-container>
                      </div>
                    </div>

                    <dl class="mt-3 space-y-2 border-t border-gray-100 pt-3">
                      <div>
                        <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Bestehende Belegschaft</dt>
                        <dd class="mt-0.5 text-sm" data-test="existing-staff-value">
                          <ng-container [ngTemplateOutlet]="existingStaffValue"
                                        [ngTemplateOutletContext]="{$implicit: r}"></ng-container>
                        </dd>
                      </div>
                      <div>
                        <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Neu &amp; Wiedereintritt</dt>
                        <dd class="mt-0.5 text-sm" data-test="new-staff-value">
                          <ng-container [ngTemplateOutlet]="newStaffValue"
                                        [ngTemplateOutletContext]="{$implicit: r}"></ng-container>
                        </dd>
                      </div>
                    </dl>

                    @if (editingId === r.id && editDraft) {
                      <div class="mt-3 border-t border-gray-100 pt-3">
                        <ng-container [ngTemplateOutlet]="rowEditor"
                                      [ngTemplateOutletContext]="{$implicit: r, prefix: 'edit-card'}"></ng-container>
                      </div>
                    }
                  </li>
                }
              </ul>
            }
          </div>
        }
      </div>
    </div>

    <!--
      One assignment's two values and its actions, defined once and rendered by BOTH layouts (the md+ table and
      the card fallback). Duplicating this markup instead would be two places for the next change to miss.
    -->

    <!--
      The existing-staff cohort. Their window is measured from the assignment's own effective-from date, so it
      ends on ONE shared day and may therefore be shown as a date. The branch is taken on the POLICY first and
      the deadline only inside it, which is what keeps a stale existingStaffDeadline on a "Sofort sperren" row
      from reading as a window those employees do not have.
      "Frist von N Tagen" is the fallback for a stored window whose deadline the server has not (yet) computed:
      it states the length without inventing a day. It is still a running window, so it is still amber.

      One badge per cell, carrying the WHOLE value — never a badge plus a grey qualifier beside it. Two colours,
      two meanings, and nothing else on this list is coloured: red = blocked right now, amber = a window is
      running. The contrast pairs are measured, not assumed (style guide, "Status badge / pill"):
      amber-700 on amber-50 = 4.84:1, red-700 on red-50 = 5.91:1 — both clear AA's 4.5:1 at this text-xs size,
      where the obvious amber-600/red-600 would not (3.07:1 / 4.41:1). Pinned by the color-contrast axe checks
      in this component's spec.
    -->
    <ng-template #existingStaffValue let-r>
      @if (existingPolicyOf(r) === Policy.Grace) {
        <span data-test="policy-badge" data-policy="window" [title]="existingHint(r)"
              class="inline-flex items-center align-top whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
          @if (existingDeadlineOf(r); as deadline) {
            Frist bis {{ deadline | date: 'dd.MM.yyyy' }}
          } @else {
            Frist von {{ graceDaysOf(r) }} Tagen
          }
        </span>
      } @else {
        <span data-test="policy-badge" data-policy="locked" [title]="existingHint(r)"
              class="inline-flex items-center align-top whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">Sofort gesperrt</span>
      }
    </ng-template>

    <!--
      The new/returning cohort. Their window starts when THEY start, so it is only ever a duration — a fixed
      date here would be wrong for every single person it was shown to. Same two colours as above, meaning the
      same two things: the colour is readable across the whole column, not per cohort.
    -->
    <ng-template #newStaffValue let-r>
      @if (newPolicyOf(r) === Policy.Grace) {
        <span data-test="policy-badge" data-policy="window" [title]="newHint(r)"
              class="inline-flex items-center align-top whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">{{ graceDaysOf(r) }} Tage ab Eintritt</span>
      } @else {
        <span data-test="policy-badge" data-policy="locked" [title]="newHint(r)"
              class="inline-flex items-center align-top whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">Sofort gesperrt</span>
      }
    </ng-template>

    <ng-template #rowActions let-r>
      <button type="button" (click)="toggleEdit(r)" data-test="edit-requirement"
              [attr.aria-expanded]="editingId === r.id"
              [attr.aria-label]="'Zuweisung für ' + targetName(r) + ' bearbeiten'"
              title="Bearbeiten"
              class="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 20h4l10-10a2.83 2.83 0 10-4-4L4 16v4z" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6"/>
        </svg>
      </button>
      <button type="button" (click)="remove(r)"
              [attr.aria-label]="'Zuweisung für ' + targetName(r) + ' entfernen'" title="Entfernen"
              class="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-7 0v12a1 1 0 001 1h6a1 1 0 001-1V7" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6"/>
        </svg>
      </button>
    </ng-template>

    <!--
      The in-place editor: the SAME fields as the "Neue Zuweisung" card, from the same gatingFields template.
      Both layouts render it, so the field ids are keyed on "prefix" (edit / edit-card) — the two copies would
      otherwise collide on an id, which is a real axe failure and not only a tidiness point. They share one
      editDraft, so they cannot drift apart.
    -->
    <ng-template #rowEditor let-r let-prefix="prefix">
      <div class="space-y-3" data-test="requirement-editor">
        <ng-container [ngTemplateOutlet]="gatingFields"
                      [ngTemplateOutletContext]="{$implicit: editDraft, prefix: prefix}"></ng-container>
        <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button type="button" (click)="cancelEdit()"
                  class="inline-flex items-center justify-center px-4 py-2.5 border-[1px] border-gray-200 rounded-2xl text-gray-600 hover:bg-gray-50 hover:border-gray-300 font-semibold shadow-sm transition-all duration-200 active:scale-95">
            Abbrechen
          </button>
          <button type="button" (click)="saveEdit(r)" [disabled]="!canSaveEdit" data-test="save-requirement"
                  class="inline-flex items-center justify-center px-5 py-2.5 bg-theme-primary-600 text-white rounded-2xl hover:bg-theme-primary-700 font-semibold shadow-lg shadow-theme-primary-100 hover:shadow-xl transition-all duration-200 active:scale-95 disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed">
            Speichern
          </button>
        </div>
      </div>
    </ng-template>

    <!--
      The gating settings, defined once and rendered three times (new assignment / the table's row editor / the
      card fallback's row editor). "prefix" keeps the label-for / input-id pairs unique between the instances,
      which is what keeps axe happy when more than one is in the DOM at the same time.
    -->
    <ng-template #gatingFields let-draft let-prefix="prefix">
      <div class="space-y-3">
        <p class="text-sm font-bold text-gray-700 tracking-tight">Bei fehlender Einreichung</p>
        <!-- The cohort boundary, spelled out. Without it a manager has to guess where "bestehend" ends. -->
        <p class="text-xs text-gray-600">
          Stichtag ist der Beginn dieser Zuweisung. Zur bestehenden Belegschaft zählt, wer seit davor ohne
          Unterbrechung beschäftigt ist. Wer danach eintritt oder nach einer Unterbrechung zurückkehrt, gilt als
          neu.
        </p>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-bold text-gray-700 mb-2 tracking-tight">Bestehende Belegschaft</label>
            <cqme-select class="block w-full" [attr.data-testid]="prefix + '-existing-policy'"
                         [options]="policyOptions" [labelKey]="'label'" [valueKey]="'value'"
                         size="md" ariaLabel="Bestehende Belegschaft"
                         [current]="draft.existingStaffPolicy"
                         (currentChange)="setExistingPolicy(draft, $event)"></cqme-select>
            <p class="mt-2 text-xs text-gray-600" [attr.data-test]="prefix + '-existing-hint'">
              {{ existingHintFor(draft) }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-bold text-gray-700 mb-2 tracking-tight">Neu eingetretene und zurückkehrende Mitarbeiter</label>
            <cqme-select class="block w-full" [attr.data-testid]="prefix + '-new-policy'"
                         [options]="policyOptions" [labelKey]="'label'" [valueKey]="'value'"
                         size="md" ariaLabel="Neu eingetretene und zurückkehrende Mitarbeiter"
                         [current]="draft.newStaffPolicy"
                         (currentChange)="setNewPolicy(draft, $event)"></cqme-select>
            <p class="mt-2 text-xs text-gray-600" [attr.data-test]="prefix + '-new-hint'">
              {{ newHintFor(draft) }}
            </p>
          </div>
        </div>

        <!--
          Never hidden and never disabled, even with both cohorts on "Sofort sperren": this same period is the
          window an employee gets when the form is republished or their answer expires, so it is a live setting
          on every assignment. Zero is not offered at all — the server answers 400 for it, and "Sofort sperren"
          is how you say it here.
        -->
        <div>
          <label class="block text-sm font-bold text-gray-700 mb-2 tracking-tight"
                 [attr.for]="prefix + '-grace-days'">Fristlänge</label>
          <div class="flex items-center gap-2">
            <input type="number" inputmode="numeric" step="1" [attr.min]="GraceMin" [attr.max]="GraceMax"
                   [id]="prefix + '-grace-days'" [attr.data-test]="prefix + '-grace-days'"
                   [ngModel]="draft.graceDays" (ngModelChange)="draft.graceDays = $event"
                   [attr.aria-invalid]="graceDaysInvalid(draft)"
                   [attr.aria-describedby]="prefix + '-grace-days-hint'"
                   class="block w-28 px-3 py-2.5 rounded-xl border-[1px] bg-white shadow-sm sm:text-sm transition-all duration-200 focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500"
                   [class.border-red-300]="graceDaysInvalid(draft)"
                   [class.border-gray-200]="!graceDaysInvalid(draft)"/>
            <span class="text-sm text-gray-600">Tage</span>
          </div>
          <p class="mt-2 text-xs text-gray-600" [id]="prefix + '-grace-days-hint'">
            Gilt für jede hier gewährte Frist und für die erneute Prüfung, wenn das Formular geändert wird oder
            eine Antwort ihre Gültigkeit verliert.
          </p>
          @if (graceDaysInvalid(draft)) {
            <p class="mt-2 flex items-center space-x-1 text-xs font-semibold text-red-600"
               [attr.data-test]="prefix + '-grace-days-error'" role="alert">
              Bitte eine ganze Zahl zwischen {{ GraceMin }} und {{ GraceMax }} eintragen.
            </p>
          }
        </div>
      </div>
    </ng-template>
  `
})
export class FormRequirementsComponent implements OnInit {
  protected readonly TargetType = RequirementTargetType;
  protected readonly Policy = BackfillPolicy;
  protected readonly GraceMin = GRACE_DAYS_MIN;
  protected readonly GraceMax = GRACE_DAYS_MAX;

  /**
   * The two options every cohort dropdown offers, in display order — the default (`Immediate`) first.
   *
   * A single stable array, never rebuilt per change-detection pass: `<cqme-select>` hands `[options]` straight
   * to ng-select, which rebuilds its item list whenever the array identity changes — including while the
   * manager has the panel open.
   */
  protected readonly policyOptions: PolicyOption[] = [
    {value: BackfillPolicy.Immediate, label: backfillPolicyLabel(BackfillPolicy.Immediate)},
    {value: BackfillPolicy.Grace, label: backfillPolicyLabel(BackfillPolicy.Grace)}
  ];

  protected loading = true;
  protected error = false;
  protected submitting = false;
  protected formTitle = '';
  /** Public for templates + tests. */
  requirements: FormRequirementResource[] = [];

  protected targetType: RequirementTargetType = RequirementTargetType.Employee;
  /**
   * The picked target's id, or `undefined` for "nothing picked yet"; public for templates + tests.
   *
   * `undefined` rather than the old `''`: an empty string is a *value* as far as ng-select is concerned, so it
   * would render an empty selection and swallow the "Bitte wählen…" placeholder.
   */
  selectedTargetId: string | undefined = undefined;

  /**
   * The gating settings the next assignment is created with. Both cohorts default to
   * {@link BackfillPolicy.Immediate} — the pre-existing behaviour — so a manager opts *into* a window, never out
   * of one by accident.
   */
  newAssignment: RequirementGatingDraft = defaultGatingDraft();

  /** The requirement currently open for in-place editing, or `null`. Public for templates + tests. */
  editingId: string | null = null;
  /** The working copy behind that editor; nothing is written back until "Speichern". */
  editDraft: RequirementGatingDraft | null = null;

  private formKey = '';
  private employees: Target[] = [];
  private relationships: Target[] = [];
  private employeeName: Record<string, string> = {};
  private relationshipName: Record<string, string> = {};

  /**
   * The target picker, so a completed assignment (or a switch of target type) can send it back to its
   * placeholder. Clearing the bound `[current]` alone is not reliable here — {@link SelectComponent.reset}
   * documents why — so the two are done together.
   *
   * Resolved by **template reference**, not by type: the cohort dropdowns are `SelectComponent`s too, and a
   * bare `@ViewChild(SelectComponent)` would silently start resetting whichever one happens to come first in
   * the view.
   */
  @ViewChild('targetPicker') private targetPicker?: SelectComponent<Target, string>;

  constructor(
    private route: ActivatedRoute,
    private forms: FormHttpService,
    private requirementHttp: FormRequirementHttpService,
    private employeeHttp: EmployeeHttpService,
    private relationshipHttp: EmploymentRelationshipHttpService,
    private alert: GlobalAlertService
  ) {
  }

  ngOnInit(): void {
    this.formKey = this.route.snapshot.queryParamMap.get('id') ?? '';
    this.load();
  }

  load(): void {
    if (!this.formKey) {
      this.error = true;
      this.loading = false;
      return;
    }
    this.loading = true;
    this.error = false;

    forkJoin({
      definition: this.forms.getDefinition(this.formKey),
      requirements: this.requirementHttp.list(this.formKey),
      // stats=false: the picker maps id → "Vorname Nachname" and nothing else (#457).
      employees: this.employeeHttp.listNl(undefined, false, false),
      relationships: this.relationshipHttp.List()
    }).subscribe({
      next: ({definition, requirements, employees, relationships}) => {
        this.formTitle = definition?.title ?? 'dieses Formular';
        this.requirements = requirements;
        this.employees = employees.map(e => ({id: e.id, name: `${e.firstName} ${e.lastName}`.trim() || e.name}));
        this.relationships = relationships.map(r => ({id: r.id, name: r.name}));
        this.employeeName = Object.fromEntries(this.employees.map(e => [e.id, e.name]));
        this.relationshipName = Object.fromEntries(this.relationships.map(r => [r.id, r.name]));
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  setTargetType(type: RequirementTargetType): void {
    this.targetType = type;
    this.clearPicker();
  }

  /**
   * Store the target the manager picked, or `undefined` when they cleared the control.
   *
   * The parameter is wider than what is stored because `<cqme-select>`'s `currentChange` is typed for the
   * multi-select case too; an array can never arrive from this single picker and is ignored rather than
   * written as a broken id. Clearing the control reports `null` (ng-select's own "no value"), which is
   * normalised to `undefined` so "nothing picked" has exactly one representation here.
   */
  setSelectedTarget(id: string | string[] | null | undefined): void {
    if (Array.isArray(id)) return;
    this.selectedTargetId = id ?? undefined;
  }

  // ----- gating settings -------------------------------------------------------------------------------------

  /** Set the existing-staff policy on a draft, ignoring the multi-select shape `currentChange` is typed for. */
  setExistingPolicy(draft: RequirementGatingDraft, value: BackfillPolicy | BackfillPolicy[] | null | undefined): void {
    const policy = this.singlePolicy(value);
    if (policy !== null) draft.existingStaffPolicy = policy;
  }

  /** Set the new-staff policy on a draft. */
  setNewPolicy(draft: RequirementGatingDraft, value: BackfillPolicy | BackfillPolicy[] | null | undefined): void {
    const policy = this.singlePolicy(value);
    if (policy !== null) draft.newStaffPolicy = policy;
  }

  /**
   * Whether the window length in `draft` is one the server would refuse.
   *
   * Checked client-side because the failure is otherwise a 400 with nothing on screen pointing at the field —
   * and because `0`, the value a manager reaches for when they mean "no window", is exactly such a 400.
   */
  graceDaysInvalid(draft: RequirementGatingDraft): boolean {
    return !isValidGraceDays(draft.graceDays);
  }

  /** The consequence line under the existing-staff dropdown, for the *draft* being edited. */
  existingHintFor(draft: RequirementGatingDraft): string {
    return existingStaffPolicyHint(draft.existingStaffPolicy, draft.graceDays ?? GRACE_DAYS_DEFAULT);
  }

  /** The consequence line under the new-staff dropdown — relative wording only, never a date. */
  newHintFor(draft: RequirementGatingDraft): string {
    return newStaffPolicyHint(draft.newStaffPolicy, draft.graceDays ?? GRACE_DAYS_DEFAULT);
  }

  /** Ready to create: a target is picked, nothing is in flight, and the window length is acceptable. */
  get canSubmitNew(): boolean {
    return !!this.selectedTargetId && !this.submitting && !this.graceDaysInvalid(this.newAssignment);
  }

  /** Ready to save the open editor. */
  get canSaveEdit(): boolean {
    return !!this.editDraft && !this.submitting && !this.graceDaysInvalid(this.editDraft);
  }

  // ----- reading a stored requirement ------------------------------------------------------------------------

  /** A requirement's existing-staff policy, defaulting a server that omits it to `Immediate`. */
  existingPolicyOf(r: FormRequirementResource): BackfillPolicy {
    return r.existingStaffPolicy ?? BackfillPolicy.Immediate;
  }

  /** A requirement's new-staff policy, same default. */
  newPolicyOf(r: FormRequirementResource): BackfillPolicy {
    return r.newStaffPolicy ?? BackfillPolicy.Immediate;
  }

  /** A requirement's window length, defaulting to the 14 days that were once fixed. */
  graceDaysOf(r: FormRequirementResource): number {
    return r.graceDays ?? GRACE_DAYS_DEFAULT;
  }

  /**
   * The one shared deadline for the existing-staff cohort, or `null` when there is none to show.
   *
   * Gated on the policy as well as on the value: a stale `existingStaffDeadline` left on a row whose policy is
   * `Immediate` must not be rendered — those employees have no window at all, and a date beside them would read
   * as one.
   */
  existingDeadlineOf(r: FormRequirementResource): string | null {
    return this.existingPolicyOf(r) === BackfillPolicy.Grace ? (r.existingStaffDeadline ?? null) : null;
  }

  /** Tooltip for the existing-staff chip. */
  existingHint(r: FormRequirementResource): string {
    return existingStaffPolicyHint(this.existingPolicyOf(r), this.graceDaysOf(r));
  }

  /** Tooltip for the new-staff chip. */
  newHint(r: FormRequirementResource): string {
    return newStaffPolicyHint(this.newPolicyOf(r), this.graceDaysOf(r));
  }

  /**
   * Targets of the current type not already assigned to this form.
   *
   * Memoised on *content*, not rebuilt per change-detection pass: `<cqme-select>` hands `[options]` straight to
   * ng-select, which rebuilds its internal item list whenever that array's identity changes. A fresh array
   * every pass would do exactly that *while the manager has the dropdown open*. Content is what invalidates it,
   * because both halves still move under this getter — the roster arrives asynchronously, and assigning or
   * removing a requirement changes who is left to offer.
   */
  get availableTargets(): Target[] {
    const pool = this.targetType === RequirementTargetType.Employee ? this.employees : this.relationships;
    const assigned = new Set(this.requirements
      .filter(r => r.targetType === this.targetType)
      .map(r => r.targetType === RequirementTargetType.Employee ? r.employeeId : r.employmentRelationshipId));
    const key = `${this.targetType}|${pool.map(t => `${t.id}:${t.name}`).join(',')}|${[...assigned].sort().join(',')}`;
    if (key !== this.availableTargetsKey) {
      this.availableTargetsKey = key;
      this.availableTargetsCache = pool.filter(t => !assigned.has(t.id));
    }
    return this.availableTargetsCache;
  }

  private availableTargetsCache: Target[] = [];
  /** `null` rather than `''`, so an initially empty pool still populates the cache once. */
  private availableTargetsKey: string | null = null;

  /** Forget the current pick, in the model *and* in the control's own view. */
  private clearPicker(): void {
    this.selectedTargetId = undefined;
    this.targetPicker?.reset();
  }

  /** Normalise `<cqme-select>`'s multi-capable output down to one policy, or `null` for "no value". */
  private singlePolicy(value: BackfillPolicy | BackfillPolicy[] | null | undefined): BackfillPolicy | null {
    if (Array.isArray(value)) return null;
    return value ?? null;
  }

  /**
   * What kind of thing a requirement points at, as the secondary line under the name.
   *
   * Secondary text rather than the chip it used to be: it is a *classification* of the row's subject, not a
   * state, and as a coloured pill it competed with the name it was meant to qualify.
   */
  targetTypeLabel(r: FormRequirementResource): string {
    return r.targetType === RequirementTargetType.Employee ? 'Mitarbeiter' : 'Arbeitsverhältnis';
  }

  targetName(r: FormRequirementResource): string {
    return r.targetType === RequirementTargetType.Employee
      ? (this.employeeName[r.employeeId ?? ''] ?? r.employeeId ?? '–')
      : (this.relationshipName[r.employmentRelationshipId ?? ''] ?? r.employmentRelationshipId ?? '–');
  }

  add(): void {
    if (!this.canSubmitNew || !this.selectedTargetId) return;
    const draft = this.newAssignment;
    const graceDays = draft.graceDays ?? GRACE_DAYS_DEFAULT;
    this.submitting = true;
    this.requirementHttp.create({
      formKey: this.formKey, targetType: this.targetType, targetId: this.selectedTargetId, mandatory: true,
      existingStaffPolicy: draft.existingStaffPolicy,
      newStaffPolicy: draft.newStaffPolicy,
      graceDays
    }).subscribe({
      next: created => {
        // A server that echoes nothing back for a field would leave the new row reading as "Sofort sperren";
        // keep what was asked for underneath whatever it did answer.
        this.requirements = [{
          existingStaffPolicy: draft.existingStaffPolicy,
          newStaffPolicy: draft.newStaffPolicy,
          graceDays,
          ...created
        }, ...this.requirements];
        this.clearPicker();
        this.submitting = false;
        this.alert.createAndShow('Zugewiesen', 'Die Pflicht-Zuweisung wurde gespeichert.', AlertLevel.success, 2000);
      },
      error: err => {
        this.submitting = false;
        this.alert.createAndShow('Fehler',
          err?.status === 403 ? 'Keine Berechtigung für diese Aktion.' : 'Die Zuweisung konnte nicht gespeichert werden.',
          AlertLevel.error, 3000);
      }
    });
  }

  /** Open the in-place editor for a requirement, or close it again if it is the one already open. */
  toggleEdit(r: FormRequirementResource): void {
    if (this.editingId === r.id) {
      this.cancelEdit();
      return;
    }
    this.editingId = r.id;
    this.editDraft = gatingDraftOf(r);
  }

  /** Close the editor, discarding the working copy. */
  cancelEdit(): void {
    this.editingId = null;
    this.editDraft = null;
  }

  /**
   * Persist the open editor's settings.
   *
   * `start`/`end` ride along untouched on purpose: the server assigns both unconditionally, so omitting them
   * would clear the assignment's temporal validity as a side effect of changing a policy — and with it the
   * effective-from date every existing-staff deadline is measured from.
   */
  saveEdit(r: FormRequirementResource): void {
    if (!this.canSaveEdit || !this.editDraft) return;
    const draft = this.editDraft;
    const graceDays = draft.graceDays ?? GRACE_DAYS_DEFAULT;
    this.submitting = true;
    this.requirementHttp.update(r.id, {
      start: r.start ?? null,
      end: r.end ?? null,
      mandatory: r.mandatory,
      existingStaffPolicy: draft.existingStaffPolicy,
      newStaffPolicy: draft.newStaffPolicy,
      graceDays
    }).subscribe({
      next: updated => {
        // Same reasoning as `add()`: the manager's choice is what the row must show, whatever the server echoed.
        const merged: FormRequirementResource = {
          ...r,
          ...updated,
          existingStaffPolicy: updated.existingStaffPolicy ?? draft.existingStaffPolicy,
          newStaffPolicy: updated.newStaffPolicy ?? draft.newStaffPolicy,
          graceDays: updated.graceDays ?? graceDays
        };
        this.requirements = this.requirements.map(x => x.id === r.id ? merged : x);
        this.cancelEdit();
        this.submitting = false;
        this.alert.createAndShow('Gespeichert', 'Die Pflicht-Zuweisung wurde aktualisiert.', AlertLevel.success, 2000);
      },
      error: err => {
        this.submitting = false;
        this.alert.createAndShow('Fehler',
          err?.status === 403 ? 'Keine Berechtigung für diese Aktion.' : 'Die Zuweisung konnte nicht gespeichert werden.',
          AlertLevel.error, 3000);
      }
    });
  }

  remove(r: FormRequirementResource): void {
    if (!confirm(`Zuweisung für "${this.targetName(r)}" entfernen?`)) return;
    this.requirementHttp.delete(r.id).subscribe({
      next: () => {
        this.requirements = this.requirements.filter(x => x.id !== r.id);
        if (this.editingId === r.id) this.cancelEdit();
        this.alert.createAndShow('Entfernt', 'Die Zuweisung wurde entfernt.', AlertLevel.success, 2000);
      },
      error: err => this.alert.createAndShow('Fehler',
        err?.status === 403 ? 'Keine Berechtigung für diese Aktion.' : 'Die Zuweisung konnte nicht entfernt werden.',
        AlertLevel.error, 3000)
    });
  }
}
